import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import './AdminListings.css';

const AdminListings = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    price: '',
    address: '',
    city: '',
    state: 'CA',
    zip: '',
    beds: '',
    baths: '',
    sqft: '',
    lotSize: '',
    description: '',
    image: '',
    images: [],
    status: 'For Sale',
    listingDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    pendingDate: '',
    display: true
  });
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const ADMIN_PASSWORD = 'gita2024admin';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      loadProperties();
    } else {
      setAuthError('Incorrect password');
    }
  };

  const loadProperties = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddImage = () => {
    if (currentImageUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, currentImageUrl.trim()]
      }));
      setCurrentImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));

    if (dragIndex === dropIndex) return;

    const newImages = [...formData.images];
    const draggedImage = newImages[dragIndex];

    // Remove from old position
    newImages.splice(dragIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);

    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const calculateDaysOnMarket = (listingDate, pendingDate) => {
    if (!listingDate) return 0;

    const startDate = new Date(listingDate + 'T00:00:00');
    const endDate = pendingDate ? new Date(pendingDate + 'T00:00:00') : new Date();

    // Reset hours to start of day for accurate day calculation
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate - startDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : 0;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        const filePath = `property-images/${fileName}`;

        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 0
        }));

        // Upload file to Supabase Storage
        const { data, error } = await supabase.storage
          .from('properties')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error('Upload error:', error);
          alert(`Failed to upload ${file.name}: ${error.message}`);
          continue;
        }

        // Get public URL
        const { data: publicUrlData } = supabase.storage
          .from('properties')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrlData.publicUrl);

        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 100
        }));
      }

      // Add uploaded URLs to images array
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));

      alert(`Successfully uploaded ${uploadedUrls.length} image(s)!`);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress({});
      // Reset file input
      e.target.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      price: '',
      address: '',
      city: '',
      state: 'CA',
      zip: '',
      beds: '',
      baths: '',
      sqft: '',
      lotSize: '',
      description: '',
      image: '',
      images: [],
      status: 'For Sale',
      listingDate: new Date().toISOString().split('T')[0],
      pendingDate: '',
      display: true
    });
    setCurrentImageUrl('');
    setEditingProperty(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Form submitted with data:', formData);

    // Validate required fields
    if (!formData.price || !formData.address || !formData.city || !formData.zip) {
      alert('Please fill in all required fields (Price, Address, City, ZIP)');
      return;
    }

    console.log('Validation passed, attempting to save...');

    try {
      if (editingProperty) {
        // Update existing property
        const { error } = await supabase
          .from('properties')
          .update({
            price: parseFloat(formData.price),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            beds: parseInt(formData.beds),
            baths: parseFloat(formData.baths),
            sqft: parseInt(formData.sqft),
            lot_size: parseInt(formData.lotSize),
            description: formData.description,
            image: formData.images.length > 0 ? formData.images[0] : formData.image,
            images: formData.images,
            status: formData.status,
            listing_date: formData.listingDate,
            pending_date: formData.pendingDate || null,
            display: formData.display
          })
          .eq('id', editingProperty.id);

        if (error) throw error;
        alert('Property updated successfully!');
      } else {
        // Create new property
        const { error } = await supabase
          .from('properties')
          .insert([{
            price: parseFloat(formData.price),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            beds: parseInt(formData.beds),
            baths: parseFloat(formData.baths),
            sqft: parseInt(formData.sqft),
            lot_size: parseInt(formData.lotSize),
            description: formData.description,
            image: formData.images.length > 0 ? formData.images[0] : formData.image,
            images: formData.images,
            status: formData.status,
            listing_date: formData.listingDate,
            pending_date: formData.pendingDate || null,
            display: formData.display
          }]);

        if (error) throw error;
        alert('Property added successfully!');
      }

      resetForm();
      loadProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      alert('Failed to save property: ' + error.message);
    }
  };

  const handleEdit = (property) => {
    setFormData({
      price: property.price ? property.price.toString() : '',
      address: property.address || '',
      city: property.city || '',
      state: property.state || 'CA',
      zip: property.zip || '',
      beds: property.beds ? property.beds.toString() : '',
      baths: property.baths ? property.baths.toString() : '',
      sqft: property.sqft ? property.sqft.toString() : '',
      lotSize: property.lot_size ? property.lot_size.toString() : '',
      description: property.description || '',
      image: property.image || '',
      images: property.images || [],
      status: property.status || 'For Sale',
      listingDate: property.listing_date || new Date().toISOString().split('T')[0],
      pendingDate: property.pending_date || '',
      display: property.display !== undefined ? property.display : true
    });
    setEditingProperty(property);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setProperties(properties.filter(property => property.id !== id));
      alert('Property deleted successfully!');
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('Failed to delete property');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h2>Admin Login - Property Listings</h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="password-input"
            />
            {authError && <p className="error-message">{authError}</p>}
            <button type="submit" className="login-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-listings">
      <div className="admin-header">
        <h1>Property Listings Management</h1>
        <div className="header-actions">
          <button onClick={() => setShowForm(!showForm)} className="add-btn">
            {showForm ? 'Cancel' : 'Add New Property'}
          </button>
          <button onClick={() => setIsAuthenticated(false)} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      {showForm && (
        <div className="property-form-container">
          <h2>{editingProperty ? 'Edit Property' : 'Add New Property'}</h2>
          <form onSubmit={handleSubmit} className="property-form">
            <div className="form-row">
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="3950000"
                  required
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="For Sale">For Sale</option>
                  <option value="Pending Sale">Pending Sale</option>
                  <option value="Recently Sold">Recently Sold</option>
                  <option value="Sold">Sold</option>
                </select>
              </div>
              <div className="form-group">
                <label>Display on Listings Page</label>
                <select
                  name="display"
                  value={formData.display}
                  onChange={(e) => setFormData(prev => ({ ...prev, display: e.target.value === 'true' }))}
                >
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
                <small>Control whether this property appears on the public listings page</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Los Altos"
                  required
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  maxLength="2"
                />
              </div>
              <div className="form-group">
                <label>ZIP *</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  placeholder="94022"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Beds</label>
                <input
                  type="number"
                  name="beds"
                  value={formData.beds}
                  onChange={handleInputChange}
                  placeholder="4"
                />
              </div>
              <div className="form-group">
                <label>Baths</label>
                <input
                  type="number"
                  step="0.5"
                  name="baths"
                  value={formData.baths}
                  onChange={handleInputChange}
                  placeholder="3.5"
                />
              </div>
              <div className="form-group">
                <label>Sqft</label>
                <input
                  type="number"
                  name="sqft"
                  value={formData.sqft}
                  onChange={handleInputChange}
                  placeholder="3200"
                />
              </div>
              <div className="form-group">
                <label>Lot Size</label>
                <input
                  type="number"
                  name="lotSize"
                  value={formData.lotSize}
                  onChange={handleInputChange}
                  placeholder="8500"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Property Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter a detailed description of the property..."
                  rows="5"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'inherit', fontSize: '14px' }}
                />
                <small>Add details about the property, features, amenities, etc.</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Listing Date</label>
                <input
                  type="date"
                  name="listingDate"
                  value={formData.listingDate}
                  onChange={handleInputChange}
                />
                <small>Date the property was listed for sale</small>
              </div>
              {(formData.status === 'Pending Sale' || formData.status === 'Recently Sold' || formData.status === 'Sold') && (
                <div className="form-group">
                  <label>Pending/Contract Date</label>
                  <input
                    type="date"
                    name="pendingDate"
                    value={formData.pendingDate}
                    onChange={handleInputChange}
                  />
                  <small>Date the property went to pending/contract</small>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Property Images</label>

                <div className="upload-section">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                  <label htmlFor="file-upload" className={`upload-btn ${uploading ? 'uploading' : ''}`}>
                    {uploading ? 'Uploading...' : 'Upload Images from Computer'}
                  </label>
                  <small>Click to select multiple images from your computer</small>
                </div>

                <div className="divider">
                  <span>OR</span>
                </div>

                <div className="image-input-container">
                  <input
                    type="text"
                    value={currentImageUrl}
                    onChange={(e) => setCurrentImageUrl(e.target.value)}
                    placeholder="https://images.unsplash.com/photo-..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddImage();
                      }
                    }}
                  />
                  <button type="button" onClick={handleAddImage} className="add-image-btn">
                    Add URL
                  </button>
                </div>
                <small>Or enter image URLs one at a time</small>

                {formData.images.length > 0 && (
                  <div className="images-preview">
                    <h4>Added Images ({formData.images.length})</h4>
                    <small style={{ display: 'block', marginBottom: '10px', color: '#666' }}>
                      Drag and drop images to reorder them
                    </small>
                    <div className="images-list">
                      {formData.images.map((url, index) => (
                        <div
                          key={index}
                          className="image-preview-item"
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                        >
                          <img src={url} alt={`Property ${index + 1}`} />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="remove-image-btn"
                          >
                            ×
                          </button>
                          <span className="image-number">{index + 1}</span>
                          <div className="drag-handle">☰</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
              <button type="submit" className="submit-btn">
                {editingProperty ? 'Update Property' : 'Add Property'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="loading">Loading properties...</p>
      ) : properties.length === 0 ? (
        <p className="no-properties">No properties yet. Click "Add New Property" to get started.</p>
      ) : (
        <div className="properties-list">
          {properties.map((property) => (
            <div key={property.id} className="property-card-admin">
              <div className="property-image-section">
                {property.image ? (
                  <img src={property.image} alt={property.address} />
                ) : (
                  <div className="no-image">No Image</div>
                )}
              </div>
              <div className="property-info-section">
                <div className="property-header">
                  <h3>${property.price.toLocaleString()}</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span className={`status-badge ${property.status.toLowerCase().replace(' ', '-')}`}>
                      {property.status}
                    </span>
                    <span className={`status-badge ${property.display ? 'display-on' : 'display-off'}`}>
                      {property.display ? '👁️ Visible' : '🚫 Hidden'}
                    </span>
                  </div>
                </div>
                <p className="property-address">
                  {property.address}<br />
                  {property.city}, {property.state} {property.zip}
                </p>
                <div className="property-stats">
                  <span>{property.beds || 0} bed</span>
                  <span>{property.baths || 0} bath</span>
                  <span>{property.sqft ? property.sqft.toLocaleString() : 0} sqft</span>
                  {property.lot_size && <span>Lot: {property.lot_size.toLocaleString()} sqft</span>}
                </div>
                <p className="days-on-market">
                  {calculateDaysOnMarket(property.listing_date, property.pending_date)} days on market
                </p>
                <div className="property-actions">
                  <button onClick={() => handleEdit(property)} className="edit-btn">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(property.id)} className="delete-btn">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminListings;
